import type { AgencyGraphJson } from '@saaga/shared/interfaces/agency.interface'
import type { AppDispatch } from '@saaga/shared/store/store'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { useCallback } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import type { NavigateFunction } from 'react-router-dom'
import { toast } from 'sonner'

import type { AgencyFormSchemaType } from '@/components/agencies/agency-form.schema'
import { agencyRoutes, orgRoutes } from '@/routes/org-routes.const'

import {
  type AgencyMutationFns,
  createNewAgency,
  handleSaveError,
} from './agency-save-draft.helper'

type SaveFn = (
  slug: string,
  values: AgencyFormSchemaType,
  flowData: AgencyGraphJson,
  fns: AgencyMutationFns,
  dispatch: AppDispatch
) => Promise<void>

interface UseAgencySaveCallbackOptions {
  form: UseFormReturn<AgencyFormSchemaType>
  flowDataRef: React.MutableRefObject<AgencyGraphJson>
  saveFn: SaveFn
  fns: AgencyMutationFns
  dispatch: AppDispatch
  navigate: NavigateFunction
  orgSlug: string | undefined
  isEditMode: boolean
  existingSlug: string | undefined
  errorMessage: string
  onSuccess?: () => void
}

export const useAgencySaveCallback = (opts: UseAgencySaveCallbackOptions): (() => Promise<void>) =>
  useCallback(async (): Promise<void> => {
    if (!(await opts.form.trigger())) {
      toast.error(opts.errorMessage)
      return
    }
    try {
      const values = opts.form.getValues()
      if (opts.isEditMode && opts.existingSlug) {
        await opts.saveFn(
          opts.existingSlug,
          values,
          opts.flowDataRef.current,
          opts.fns,
          opts.dispatch
        )
        trackEvent('agency_updated', { agencySlug: opts.existingSlug, agencyName: values.name })
        opts.onSuccess?.()
        return
      }
      const slug = await createNewAgency(values, opts.flowDataRef.current, opts.fns)
      trackEvent('agency_created', { agencyName: values.name, visibility: values.visibility })
      opts.navigate(
        `/${opts.orgSlug}/${orgRoutes.agencies}/${agencyRoutes.studio}/${slug}/${agencyRoutes.edit}`,
        { replace: true }
      )
    } catch (error) {
      handleSaveError(error, opts.isEditMode)
    }
  }, [opts])
